/**
 * Usage & cost — what the AI layer consumed this month, what it cost, and what a
 * lead worth calling cost. Owner and Admin.
 *
 * Visual Reference: `design/screens/usage-cost.html` — default, loading, zero and
 * error. PRD §3.13: "plan consumption against limits · spend by day / funnel /
 * model where BYOK is in use · **cost per qualified lead** · … · budget caps (soft
 * warning + hard stop) · kill switch".
 *
 * `GET /v1/orgs/{oid}/usage` refuses Editor, Reviewer, Viewer and Client AT THE API
 * (`packages/api/test/usage-route.test.ts`), and the nav offers the page to Owner
 * and Admin only (`canSeeUsage`), so nobody is shown a page that answers 403.
 *
 * A SENTENCE FIRST (PRD §1.9): "You've spent $34.86 on AI this month." The figure
 * is the evidence under it — `lib/money.ts` formats every amount from integer
 * micros, with no float division anywhere in the path (DECISION-008).
 *
 * THE RIGHT COLUMN holds the budget caps (Story 23.4) and the stop control (Story
 * 23.5), each fed by its own route — `GET /v1/orgs/{oid}/usage/caps` and
 * `…/usage/switch` — so both still work when the figures fail to load. The export's
 * error state keeps them usable "because a broken page is exactly when an operator
 * reaches for the stop button", and its two sentences naming them are drawn now that
 * both controls exist. The zero state's "Your caps are already set, so nothing can
 * surprise you" is said only when both caps are.
 *
 * THE STOP CONTROL IS OWNER-ONLY TO PRESS. `PUT …/usage/switch` refuses an Admin at
 * the API, so an Admin is shown the state and told who can change it, never a button
 * that answers 403.
 *
 * WHAT THIS DRAWS THAT THE MOCKUP DOES NOT, and why:
 *
 *   · **Model checks** — a row in *Where it went* for spend no funnel owns. The
 *     Test-this-model button (Story 18.5) is an AI call, it costs, and without the
 *     row the funnels would not sum to the headline. It appears only when there is
 *     such spend, which in the mockup there is not.
 *   · **A model row with no payer line** — a call logged before who-paid was
 *     recorded (migration 0024). Unknown is left unsaid rather than guessed.
 *   · **No cost-per-lead clause** when nothing qualified this month — there is no
 *     per-lead figure to state, and a "$0.00" would say leads were free.
 *   · **The caps card's other states** — the stop reached, a warning with no moment
 *     to name, the caps failing to load, and a save the API refused. `lib/caps.ts`
 *     says which sentence and why; a refusal is the API's own message, under the
 *     field it names.
 *   · **The stop control's other states** — switched off, with a line saying since
 *     when and a primary "Turn AI spend back on"; an Admin's view, with no button;
 *     and the switch failing to load. The export draws the control at rest only.
 *
 * Stories 23.3, 23.4 and 23.5.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPut, ApiFailure } from '../lib/api'
import { bothCapsSet, capsSentence, switchSentence, type CapsView, type SwitchView } from '../lib/caps'
import {
  averageMicros,
  barPercent,
  dollarsToMicros,
  formatMicros,
  formatScale,
  halfOf,
  microsToDollars,
  percentOf,
  scaleTop,
} from '../lib/money'
import { busiestWeekdays, type SpendDay } from '../lib/spend'
import { capitalise, spell } from '../lib/words'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Field } from '../ui/Field'
import { Modal } from '../ui/Modal'
import { Skeleton } from '../ui/Skeleton'

type Meter = { used: number; limit: number }

type FunnelSpend = {
  funnelId: string | null
  name: string | null
  analyses: number
  costMicros: number
  qualifiedLeads: number
  perQualifiedLeadMicros: number | null
}

type ModelSpend = {
  provider: string
  model: string
  paidBy: 'plan' | 'own_key' | null
  calls: number
  costMicros: number
}

/** `GET /v1/orgs/{oid}/usage` — `packages/api/src/services/usage/index.ts`. */
export type UsageView = {
  period: { key: string; start: number; end: number }
  consumption: { submissions: Meter; analyses: Meter; seats: Meter }
  partialSubmissions: number
  spend: { costMicros: number; calls: number; qualifiedLeads: number; perQualifiedLeadMicros: number | null }
  byDay: SpendDay[]
  byFunnel: FunnelSpend[]
  byModel: ModelSpend[]
  escalation: { analyses: number; escalated: number; analysisMicros: number; escalatedMicros: number } | null
}

const DAY_MS = 86_400_000

/** `-1` is unlimited (`services/entitlements`), and has no bar to fill. */
const UNLIMITED = -1

/** The export's words, with straight apostrophes held here rather than in JSX (DECISION-118). */
const COPY = {
  zeroHeadline: "You haven't spent anything on AI yet.",
  zeroTitle: 'Nothing has been analysed yet.',
  zeroBody:
    "When your funnels start receiving submissions, this page will show what the AI layer costs you and what it's buying — by day, by funnel and by model.",
  errorHeadline: "Your spend figures didn't load.",
  errorTitle: "Couldn't load your usage",
  errorControls: 'Your caps and the stop control are underneath and still work.',
  errorBody: 'Your spend is still being metered. Load the figures again — your caps and the stop control below are unaffected.',
  escalationOnly: "Escalation only happens when the default model isn't confident enough.",
  modelChecks: 'Model checks',
  capsAlreadySet: 'Your caps are already set, so nothing can surprise you.',
  capsTitle: 'Stop it before it surprises you',
  capsGuarantee:
    'Reaching a cap stops analysis. It never stops a lead — submissions are still captured, scored on your rubric and routed, whatever your spend.',
  capsLoadFailed: "Your caps didn't load. Load them again — nothing about them has changed.",
  capsSaveFailed: "We couldn't save your caps. Check your connection and save again.",
  capAmount: 'Enter an amount in dollars, like 30 or 30.50 — or leave it empty for no cap.',
  switchTitle: 'Stop all AI spend now',
  switchBody:
    "Your funnels keep working. Leads are still captured, scored on your rubric and routed — they just won't be read by a model until you turn this back on.",
  switchRecorded: 'Turning it on and off is recorded, with who did it.',
  switchConfirmTitle: 'Stop all AI spend?',
  switchConfirmBody:
    'Analysis stops on the next queued lead. Extraction, the review queue and rationales pause until you turn it back on. Nothing already analysed is lost.',
  switchOwnerOnly: 'Only an Owner can stop AI spend or turn it back on.',
  switchLoadFailed: "The stop control didn't load. Load it again — nothing about it has changed.",
  switchFailed: "We couldn't change AI spend. Check your connection and try again.",
} as const

/** A BYOK slug, as its owner calls it (`lib/byok-routes.ts` in the API). */
const PROVIDER_NAME: Record<string, string> = {
  openai: 'OpenAI',
  'azure-openai': 'Azure OpenAI',
  openrouter: 'OpenRouter',
  groq: 'Groq',
}

const count = (value: number) => value.toLocaleString('en-GB')

/**
 * "12 Sep" — the axis labels the mockup draws. A fixed month list rather than
 * `toLocaleDateString('en-GB', { month: 'short' })`, because Chrome's ICU spells
 * September "Sept" in en-GB, so the axis read "1 Sept" against the drawing's "1 Sep"
 * and the browser's locale data would decide the label. UTC, the API's buckets.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const shortDate = (at: number) => {
  const date = new Date(at)
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`
}

/** Whole days until the month's counters reset, counting a part-day as a day. */
function daysLeft(end: number, now: number): number {
  let days = 0
  for (let at = now; at < end; at += DAY_MS) days += 1
  return days
}

const renews = (days: number) => `Renews in ${days} ${days === 1 ? 'day' : 'days'}`

export function UsageCost() {
  const { org } = useShell()
  const orgId = org?.id ?? null

  const [view, setView] = useState<UsageView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [caps, setCaps] = useState<CapsView | null>(null)
  const [capsError, setCapsError] = useState<string | null>(null)
  const [switchView, setSwitchView] = useState<SwitchView | null>(null)
  const [switchError, setSwitchError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!orgId) return
    setError(null)
    setView(null)
    try {
      setView(await apiGet<UsageView>(`/v1/orgs/${orgId}/usage`))
    } catch (e) {
      // A message from the API names the fix and is rendered as it arrives
      // (ARCH §4.3); anything else gets the export's own reassurance.
      setError(e instanceof ApiFailure ? e.message : COPY.errorBody)
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  // Its own request, so a failure of the figures above never takes the caps with it.
  const loadCaps = useCallback(async () => {
    if (!orgId) return
    setCapsError(null)
    try {
      setCaps(await apiGet<CapsView>(`/v1/orgs/${orgId}/usage/caps`))
    } catch (e) {
      setCapsError(e instanceof ApiFailure ? e.message : COPY.capsLoadFailed)
    }
  }, [orgId])

  useEffect(() => {
    void loadCaps()
  }, [loadCaps])

  // The stop control's own request, for the caps' reason.
  const loadSwitch = useCallback(async () => {
    if (!orgId) return
    setSwitchError(null)
    try {
      setSwitchView(await apiGet<SwitchView>(`/v1/orgs/${orgId}/usage/switch`))
    } catch (e) {
      setSwitchError(e instanceof ApiFailure ? e.message : COPY.switchLoadFailed)
    }
  }, [orgId])

  useEffect(() => {
    void loadSwitch()
  }, [loadSwitch])

  const now = Date.now()
  const zero = view !== null && view.spend.calls === 0
  const sentence = 'm-0 max-w-mdx-measure-52 text-mdx-18 leading-mdx-reason text-mdx-slate'
  const verdict = 'm-0 text-balance text-mdx-verdict font-mdx-bold leading-mdx-verdict tracking-mdx-verdict text-mdx-ink'

  return (
    <div className="flex flex-col gap-mdx-24">
      <header className="flex flex-col gap-mdx-10">
        <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          {org?.name ?? 'This organisation'} · this month
        </span>
        {error ? (
          <>
            <h1 className={verdict}>{COPY.errorHeadline}</h1>
            <p className={sentence}>{COPY.errorControls}</p>
          </>
        ) : view === null ? (
          <>
            <Skeleton className="h-mdx-40 rounded-mdx-control-sm" style={{ width: '46%' }} />
            <Skeleton className="h-mdx-18 rounded-mdx-control-sm" style={{ width: '52%' }} />
          </>
        ) : zero ? (
          <>
            <h1 className={verdict}>{COPY.zeroHeadline}</h1>
            <p className={sentence}>
              Your plan renews in {daysPhrase(daysLeft(view.period.end, now))}.
              {bothCapsSet(caps) ? ` ${COPY.capsAlreadySet}` : null}
            </p>
          </>
        ) : (
          <>
            <h1 className={verdict}>You&apos;ve spent {formatMicros(view.spend.costMicros)} on AI this month.</h1>
            <p className={sentence}>
              {view.spend.perQualifiedLeadMicros === null ? null : (
                <>
                  That&apos;s{' '}
                  <span className="font-mdx-semibold text-mdx-ink">
                    {formatMicros(view.spend.perQualifiedLeadMicros)}
                  </span>{' '}
                  for every lead worth calling.{' '}
                </>
              )}
              Your plan renews in {daysPhrase(daysLeft(view.period.end, now))}.
            </p>
          </>
        )}
      </header>

      <div className="grid grid-cols-1 items-start gap-mdx-20 lg:mdx-usage-split">
        <div className="flex min-w-0 flex-col gap-mdx-20">
          {error ? (
            <Notice mark="error" title={COPY.errorTitle} body={error} action="Try again" onAction={() => void load()} />
          ) : view === null ? (
            <LoadingCards />
          ) : (
            <>
              <Consumption view={view} now={now} />
              {zero ? (
                <Notice
                  title={COPY.zeroTitle}
                  body={COPY.zeroBody}
                  detail
                  action="See your funnels"
                  onAction={() => window.location.assign('/funnels')}
                />
              ) : (
                <>
                  <SpendByDay view={view} now={now} />
                  <WhereItWent view={view} />
                  <Models view={view} />
                </>
              )}
            </>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-mdx-20">
          <CapsCard
            orgId={orgId}
            caps={caps}
            error={capsError}
            onRetry={() => void loadCaps()}
            onSaved={setCaps}
            now={now}
          />
          <StopControl
            orgId={orgId}
            canChange={org?.role === 'owner'}
            view={switchView}
            error={switchError}
            onRetry={() => void loadSwitch()}
            onChanged={setSwitchView}
            now={now}
          />
        </div>
      </div>
    </div>
  )
}

const daysPhrase = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`

/* ── the cards ──────────────────────────────────────────────────────────── */

function Card({ gap = 'gap-mdx-20', children }: { gap?: string; children: React.ReactNode }) {
  return (
    <section className={`flex flex-col ${gap} rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-28 shadow-mdx-card`}>
      {children}
    </section>
  )
}

const cardTitle = 'text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink'

function Consumption({ view, now }: { view: UsageView; now: number }) {
  const { submissions, analyses, seats } = view.consumption
  return (
    <Card gap="gap-mdx-24">
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <h2 className={`m-0 ${cardTitle}`}>What you&apos;ve used</h2>
        <span className="text-mdx-13 text-mdx-slate">{renews(daysLeft(view.period.end, now))}</span>
      </div>
      <div className="flex flex-col gap-mdx-16">
        <MeterRow name="Submissions" meter={submissions} />
        <MeterRow name="Analyses" meter={analyses} />
        <MeterRow name="Seats" meter={seats} />
      </div>
      {view.partialSubmissions > 0 ? (
        <p className="m-0 max-w-mdx-measure text-mdx-14 text-mdx-slate">
          {view.partialSubmissions === 1
            ? '1 submission was partial and cost you nothing — it is not analysed until someone finishes.'
            : `${count(view.partialSubmissions)} submissions were partial and cost you nothing — they are not analysed until someone finishes.`}
        </p>
      ) : null}
    </Card>
  )
}

function MeterRow({ name, meter }: { name: string; meter: Meter }) {
  const unlimited = meter.limit === UNLIMITED
  const share = unlimited ? 0 : Math.min(100, percentOf(meter.used, meter.limit))
  return (
    <div className="flex flex-col gap-mdx-6">
      <div className="flex flex-wrap items-baseline justify-between gap-mdx-8">
        <span className="text-mdx-15 font-mdx-medium">{name}</span>
        <span className="flex items-baseline gap-mdx-10">
          {unlimited ? (
            <span className="text-mdx-15 font-mdx-bold tabular-nums">{count(meter.used)} used — no limit on this plan</span>
          ) : (
            <>
              <span className="text-mdx-15 font-mdx-bold tabular-nums">
                {count(meter.used)} of {count(meter.limit)}
              </span>
              <span className="text-mdx-14 text-mdx-slate tabular-nums">{share}%</span>
            </>
          )}
        </span>
      </div>
      <span aria-hidden="true" className="block h-mdx-8 overflow-hidden rounded-mdx-pill bg-mdx-line-soft">
        <span className="block h-mdx-8 bg-mdx-brand" style={{ width: `${share}%` }} />
      </span>
    </div>
  )
}

function SpendByDay({ view, now }: { view: UsageView; now: number }) {
  const { start, end } = view.period
  const spent = new Map(view.byDay.map((day) => [day.day, day.costMicros]))
  const slots: number[] = []
  for (let at = start; at < end; at += DAY_MS) slots.push(at)

  const highest = view.byDay.reduce((max, day) => Math.max(max, day.costMicros), 0)
  const top = scaleTop(highest)
  // An average over the days of the month that have begun, today included.
  const elapsed = slots.filter((at) => at <= now).length
  const average = averageMicros(view.spend.costMicros, Math.max(1, elapsed))
  const heading = busiestWeekdays(view.byDay)
  const summary = `Highest day ${formatMicros(highest)} · average ${formatMicros(average)}.`
  const middle = slots[Math.floor(slots.length / 2)] ?? start

  return (
    <Card>
      <div className="flex flex-col gap-mdx-8">
        <h2 className="m-0 text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">Spend by day</h2>
        {heading ? (
          <span className="text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1 text-mdx-ink">{heading}</span>
        ) : null}
      </div>
      <div className="flex flex-col gap-mdx-8">
        <div className="flex items-end gap-mdx-12">
          <div className="flex h-mdx-chart flex-none flex-col justify-between text-right text-mdx-13 text-mdx-slate tabular-nums">
            <span>{formatScale(top)}</span>
            <span>{formatScale(halfOf(top))}</span>
            <span>{formatScale(0)}</span>
          </div>
          <div
            role="img"
            aria-label={`Daily AI spend for this month.${heading ? ` ${heading}` : ''} ${summary}`}
            className="flex h-mdx-chart min-w-0 flex-1 items-end gap-mdx-3 border-b border-mdx-line"
          >
            {slots.map((at) => (
              <span
                key={at}
                className="flex-1 rounded-t-mdx-5 bg-mdx-brand"
                style={{ height: `${barPercent(spent.get(at) ?? 0, top)}%` }}
              />
            ))}
          </div>
        </div>
        <div className="flex items-baseline gap-mdx-12 text-mdx-13 text-mdx-slate">
          <span className="flex-none">{shortDate(start)}</span>
          <span className="flex-1 text-center">{shortDate(middle)}</span>
          <span className="flex-none">{shortDate(end - DAY_MS)}</span>
        </div>
      </div>
      <p className="m-0 text-mdx-14 text-mdx-slate">{summary}</p>
    </Card>
  )
}

function WhereItWent({ view }: { view: UsageView }) {
  const funnels = view.byFunnel.filter((row) => row.funnelId !== null && row.analyses > 0).length
  const analyses = view.byFunnel.reduce((sum, row) => sum + row.analyses, 0)
  const numeric = 'w-mdx-meta-column-sm flex-none text-right tabular-nums'
  // The export draws this column at 110px, which no token names. At
  // `--mdx-meta-column` (96px) its header took three lines on staging where the
  // drawing takes two, so it borrows `--mdx-field-badge` (108px) — a value match
  // under another role's name, as the dashboard's plot height borrows
  // `--mdx-menu-width` (`index.css`). The census adjudicates by value.
  const perLead = 'w-mdx-field-badge flex-none text-right tabular-nums'

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <h2 className={`m-0 ${cardTitle}`}>Where it went</h2>
        <span className="text-mdx-13 text-mdx-slate">
          {capitalise(spell(funnels))} {funnels === 1 ? 'funnel' : 'funnels'} analysed this month
        </span>
      </div>
      <div className="overflow-x-auto">
        <div role="table" aria-label="Spend by funnel" className="flex min-w-mdx-auth-card flex-col gap-mdx-2">
          <div
            role="row"
            className="flex items-end gap-mdx-12 px-mdx-16 py-mdx-8 text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate"
          >
            <span role="columnheader" className="min-w-0 flex-auto">Funnel</span>
            <span role="columnheader" className={numeric}>Analyses</span>
            <span role="columnheader" className={numeric}>Spend</span>
            <span role="columnheader" className={`${perLead} leading-mdx-heading`}>Per qualified lead</span>
          </div>
          {view.byFunnel.map((row) => (
            <div
              role="row"
              key={row.funnelId ?? 'model-checks'}
              className="flex items-center gap-mdx-12 rounded-mdx-control px-mdx-16 py-mdx-14 text-mdx-15"
            >
              <span role="cell" className="min-w-0 flex-auto font-mdx-medium">
                {row.funnelId === null ? COPY.modelChecks : (row.name ?? 'A funnel since deleted')}
              </span>
              <span role="cell" className={numeric}>{count(row.analyses)}</span>
              <span role="cell" className={numeric}>{formatMicros(row.costMicros)}</span>
              <span role="cell" className={perLead}>
                {row.perQualifiedLeadMicros === null ? '—' : formatMicros(row.perQualifiedLeadMicros)}
              </span>
            </div>
          ))}
          <div role="row" className="flex items-center gap-mdx-12 border-t border-mdx-line px-mdx-16 py-mdx-14 text-mdx-15">
            <span role="cell" className="min-w-0 flex-auto font-mdx-semibold">Total</span>
            <span role="cell" className={`${numeric} font-mdx-bold`}>{count(analyses)}</span>
            <span role="cell" className={`${numeric} font-mdx-bold`}>{formatMicros(view.spend.costMicros)}</span>
            <span role="cell" className={`${perLead} font-mdx-bold`}>
              {view.spend.perQualifiedLeadMicros === null ? '—' : formatMicros(view.spend.perQualifiedLeadMicros)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  )
}

function payerLine(row: ModelSpend): string | null {
  if (row.paidBy === 'plan') return 'Included with your plan'
  if (row.paidBy === 'own_key') return `Your own ${PROVIDER_NAME[row.provider] ?? row.provider} key`
  return null
}

function Models({ view }: { view: UsageView }) {
  const escalation = view.escalation
  const amount = 'w-mdx-72 flex-none whitespace-nowrap text-right text-mdx-15 font-mdx-bold tabular-nums'
  const calls = (n: number) => `${count(n)} ${n === 1 ? 'call' : 'calls'}`

  return (
    <Card>
      <div className="flex flex-col gap-mdx-6">
        <h2 className={`m-0 ${cardTitle}`}>Which models you used</h2>
        <span className="max-w-mdx-measure-60 text-mdx-15 text-mdx-slate">{COPY.escalationOnly}</span>
      </div>
      <div className="flex flex-col gap-mdx-8">
        {view.byModel.map((row) => {
          const payer = payerLine(row)
          return (
            <div
              key={`${row.provider}|${row.model}|${row.paidBy ?? ''}`}
              className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12"
            >
              <span className="flex min-w-0 flex-1 basis-mdx-row-identity flex-col leading-mdx-heading">
                {/* `break-words`, not `break-all`: a model id breaks at its hyphens the
                    way the export's 375 capture does, and splits a word only when
                    no hyphen fits — `break-all` cut "instruct" in two on staging. */}
                <span className="break-words text-mdx-15 font-mdx-semibold">{row.model}</span>
                {payer ? <span className="text-mdx-14 text-mdx-slate">{payer}</span> : null}
              </span>
              <span className="whitespace-nowrap text-mdx-14 text-mdx-slate tabular-nums">{calls(row.calls)}</span>
              <span className={amount}>{formatMicros(row.costMicros)}</span>
            </div>
          )
        })}
        <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 border-t border-mdx-line px-mdx-16 py-mdx-14">
          <span className="min-w-0 flex-1 basis-mdx-row-identity text-mdx-15 font-mdx-semibold">Total</span>
          <span className="whitespace-nowrap text-mdx-14 text-mdx-slate tabular-nums">{calls(view.spend.calls)}</span>
          <span className={amount}>{formatMicros(view.spend.costMicros)}</span>
        </div>
      </div>
      {escalation && escalation.analyses > 0 && escalation.escalated > 0 ? (
        <p className="m-0 text-mdx-14 text-mdx-slate">
          {percentOf(escalation.escalated, escalation.analyses)}% of your analyses escalated. They are{' '}
          {percentOf(escalation.escalatedMicros, escalation.analysisMicros)}% of your spend.
        </p>
      ) : null}
    </Card>
  )
}

/**
 * The review-coloured line a control uses to say where it stands — the export's caps
 * warning: a `--mdx-review-tint` ground, a 26px mark, 15px text. The stop control's
 * switched-off line wears it too, because being off is the operator's own choice
 * working, not an error.
 */
function StatusLine({ children }: { children: React.ReactNode }) {
  return (
    <div role="status" className="flex items-start gap-mdx-16 rounded-mdx-control bg-mdx-review-tint px-mdx-16 py-mdx-12">
      <span
        aria-hidden="true"
        className="flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm bg-mdx-review text-mdx-14 font-mdx-bold text-mdx-white"
      >
        !
      </span>
      <span className="min-w-0 text-mdx-15">{children}</span>
    </div>
  )
}

/**
 * The kill switch — Story 23.5. `usage-cost.html`: "Stop all AI spend now", a
 * SECONDARY button, "not a destructive red button, because this is reversible and
 * reversible actions are never destructive in this system", and a confirm modal whose
 * words are the export's own.
 *
 * Turning it back on asks nothing: it restores what the Owner had, and the confirm
 * exists to stop spend being cut by a stray click, not to slow its return.
 */
function StopControl({
  orgId,
  canChange,
  view,
  error,
  onRetry,
  onChanged,
  now,
}: {
  orgId: string | null
  canChange: boolean
  view: SwitchView | null
  error: string | null
  onRetry: () => void
  onChanged: (view: SwitchView) => void
  now: number
}) {
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const change = async (off: boolean) => {
    if (!orgId) return
    setSaving(true)
    setProblem(null)
    try {
      onChanged(await apiPut<SwitchView>(`/v1/orgs/${orgId}/usage/switch`, { off }))
    } catch (e) {
      setProblem(e instanceof ApiFailure ? e.message : COPY.switchFailed)
    } finally {
      setSaving(false)
      setConfirming(false)
    }
  }

  const sentence = view ? switchSentence(view, now) : null

  return (
    <Card gap="gap-mdx-16">
      <div className="flex flex-col gap-mdx-6">
        <h2 className={`m-0 ${cardTitle}`}>{COPY.switchTitle}</h2>
        <span className="max-w-mdx-measure-60 text-mdx-15 text-mdx-slate">{COPY.switchBody}</span>
      </div>
      {sentence ? <StatusLine>{sentence}</StatusLine> : null}
      {error ? (
        <div className="flex flex-col items-start gap-mdx-10">
          <Callout>{error}</Callout>
          <Button size="small" variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
      {problem ? <Callout>{problem}</Callout> : null}
      {!canChange ? (
        <span className="text-mdx-14 text-mdx-slate">{COPY.switchOwnerOnly}</span>
      ) : view?.off ? (
        <Button className="self-start" loading={saving ? 'Turning AI spend back on' : undefined} onClick={() => void change(false)}>
          Turn AI spend back on
        </Button>
      ) : (
        <Button variant="secondary" className="self-start" disabled={view === null} onClick={() => setConfirming(true)}>
          Stop AI spend
        </Button>
      )}
      <span className="text-mdx-13 text-mdx-slate">{COPY.switchRecorded}</span>
      {confirming ? (
        <Modal
          title={COPY.switchConfirmTitle}
          onClose={() => setConfirming(false)}
          actions={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button loading={saving ? 'Stopping AI spend' : undefined} onClick={() => void change(true)}>
                Stop AI spend
              </Button>
            </>
          }
        >
          <p className="m-0 max-w-mdx-measure-52 text-mdx-16">{COPY.switchConfirmBody}</p>
        </Modal>
      ) : null}
    </Card>
  )
}

type CapField = 'warnMicros' | 'stopMicros'

/**
 * The budget caps — Story 23.4. Two amounts, the month's standing against them in one
 * review-coloured sentence, and the guarantee, verbatim.
 *
 * REVIEW COLOURS, NOT ERROR ONES, for every state — the export's own note: "the caps
 * card carries the review-coloured warning rather than an error one". A cap reached
 * is the operator's own control working, and nothing is lost: RULE-060 is the line
 * printed under the fields.
 *
 * THE FIELDS ARE THE WHOLE STATE. Both amounts are sent on every save and an empty
 * field is no cap, which is what the API's body is (`routes/usage.ts`). Text that is
 * not dollars and cents is refused here, before a request, because it cannot be sent
 * as micros at all; every other rule — above zero, the warning below the stop — is
 * the API's, and its message is rendered under the field it names.
 */
function CapsCard({
  orgId,
  caps,
  error,
  onRetry,
  onSaved,
  now,
}: {
  orgId: string | null
  caps: CapsView | null
  error: string | null
  onRetry: () => void
  onSaved: (view: CapsView) => void
  now: number
}) {
  const [warn, setWarn] = useState('')
  const [stop, setStop] = useState('')
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<{ field: CapField | null; message: string } | null>(null)

  // What the API holds, back into the fields — on load and after every save.
  useEffect(() => {
    if (!caps) return
    setWarn(caps.warnMicros === null ? '' : microsToDollars(caps.warnMicros))
    setStop(caps.stopMicros === null ? '' : microsToDollars(caps.stopMicros))
  }, [caps])

  const save = async () => {
    if (!orgId) return
    const warnMicros = dollarsToMicros(warn)
    const stopMicros = dollarsToMicros(stop)
    if (warnMicros === undefined || stopMicros === undefined) {
      setProblem({ field: warnMicros === undefined ? 'warnMicros' : 'stopMicros', message: COPY.capAmount })
      return
    }
    setSaving(true)
    setProblem(null)
    try {
      onSaved(await apiPut<CapsView>(`/v1/orgs/${orgId}/usage/caps`, { warnMicros, stopMicros }))
    } catch (e) {
      if (e instanceof ApiFailure) {
        const field = e.details?.['field']
        setProblem({ field: field === 'warnMicros' || field === 'stopMicros' ? field : null, message: e.message })
      } else {
        setProblem({ field: null, message: COPY.capsSaveFailed })
      }
    } finally {
      setSaving(false)
    }
  }

  const sentence = caps ? capsSentence(caps, now) : null
  const fieldError = (field: CapField) => (problem?.field === field ? problem.message : undefined)

  return (
    <Card>
      <h2 className={`m-0 ${cardTitle}`}>{COPY.capsTitle}</h2>
      {sentence ? <StatusLine>{sentence}</StatusLine> : null}
      {error ? (
        <div className="flex flex-col items-start gap-mdx-10">
          <Callout>{error}</Callout>
          <Button size="small" variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
      {/* The export's inputs grow from 140px, which no product token names; at
          `--mdx-row-control-sm` (170px) they still sit side by side in the 1440 column
          and stack one to a row at 375, which is what the two captures draw. */}
      <div className="flex flex-wrap gap-mdx-14">
        <div className="min-w-0 flex-1 basis-mdx-row-control-sm">
          <Field
            label="Warn me at"
            prefix="$"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={warn}
            disabled={caps === null}
            onChange={(event) => setWarn(event.target.value)}
            error={fieldError('warnMicros')}
            className="tabular-nums"
          />
        </div>
        <div className="min-w-0 flex-1 basis-mdx-row-control-sm">
          <Field
            label="Stop analysing at"
            prefix="$"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={stop}
            disabled={caps === null}
            onChange={(event) => setStop(event.target.value)}
            error={fieldError('stopMicros')}
            className="tabular-nums"
          />
        </div>
      </div>
      <p className="m-0 max-w-mdx-measure-60 text-mdx-14 text-mdx-slate">{COPY.capsGuarantee}</p>
      {problem && problem.field === null ? <Callout>{problem.message}</Callout> : null}
      <Button className="self-start" disabled={caps === null} loading={saving ? 'Saving caps' : undefined} onClick={() => void save()}>
        Save caps
      </Button>
    </Card>
  )
}

/** The zero and error cards — one shape in the export: centred, a mark, a title, a way on. */
function Notice({
  title,
  body,
  action,
  onAction,
  mark = 'plain',
  detail = false,
}: {
  title: string
  body: string
  action: string
  onAction: () => void
  mark?: 'plain' | 'error'
  /** The zero state's "Cost per qualified lead · No analyses yet" line. */
  detail?: boolean
}) {
  return (
    <section className="flex flex-col items-center gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
      {mark === 'error' ? (
        <span
          aria-hidden="true"
          className="flex size-mdx-40 flex-none items-center justify-center rounded-mdx-control bg-mdx-error text-mdx-20 font-mdx-bold text-mdx-white"
        >
          !
        </span>
      ) : (
        <span aria-hidden="true" className="size-mdx-40 flex-none rounded-mdx-control bg-mdx-line" />
      )}
      <h2 className={`m-0 ${cardTitle}`}>{title}</h2>
      <span className="max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">{body}</span>
      {detail ? (
        <span className="text-mdx-14 text-mdx-slate">
          Cost per qualified lead · <span className="font-mdx-semibold text-mdx-ink">No analyses yet</span>
        </span>
      ) : null}
      <Button onClick={onAction}>{action}</Button>
    </section>
  )
}

/** Skeleton cards at the height of the real ones, and bars at the real chart height. */
function LoadingCards() {
  return (
    <div className="flex flex-col gap-mdx-20" aria-busy="true">
      <Card gap="gap-mdx-24">
        <Skeleton className="h-mdx-18 rounded-mdx-sm" style={{ width: '38%' }} />
        {(['64%', '58%', '40%'] as const).map((width) => (
          <div key={width} className="flex flex-col gap-mdx-6">
            <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width }} />
            <Skeleton className="h-mdx-8 rounded-mdx-pill" />
          </div>
        ))}
      </Card>
      <Card>
        <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width: '24%' }} />
        <div className="flex h-mdx-chart items-end gap-mdx-3">
          {/* `Skeleton` takes a width; a bar's skeleton is its HEIGHT, so it is the
              same pulse and fill written out — `ui/Skeleton.tsx`'s own classes. */}
          {['40%', '72%', '55%', '90%', '35%', '64%', '80%', '48%', '70%', '30%'].map((height, i) => (
            <span
              key={i}
              aria-hidden="true"
              className="block flex-1 animate-mdx-pulse rounded-t-mdx-5 bg-mdx-line-soft"
              style={{ height }}
            />
          ))}
        </div>
      </Card>
    </div>
  )
}
